// PostHog product analytics, error tracking and session replay.
// Nothing loads unless REACT_APP_POSTHOG_KEY is set at build time: the
// library is pulled in with a dynamic import only after the key check.

export const APP_NAME = 'gussy-website';
export const DEFAULT_HOST = 'https://us.i.posthog.com';

// Pages that show payment details or a delivery address. Session replay is
// paused and click autocapture is dropped while the visitor is on them.
const SENSITIVE_PATHS = [
  '/checkout',
  '/checkout-package',
  '/success-checkout',
  '/pending-checkout',
  '/failed-payment',
  '/cancelled-payment',
];

// Query parameters that must never reach PostHog (sign-in tokens, payment ids).
const SENSITIVE_PARAMS = ['token', 'trans_id', 'transaction_id', 'payment_intent_id', 'checkout_id'];

const URL_PROPERTIES = ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer'];

let clientPromise = null;
let replayPausedHere = false;

export const isSensitivePath = (pathname = '') =>
  SENSITIVE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export const scrubUrl = (value) => {
  if (typeof value !== 'string' || value.indexOf('?') === -1) return value;
  try {
    const url = new URL(value);
    SENSITIVE_PARAMS.forEach((param) => {
      if (url.searchParams.has(param)) url.searchParams.set(param, 'redacted');
    });
    return url.toString();
  } catch (e) {
    return value;
  }
};

export const beforeSend = (event) => {
  if (!event) return event;
  const properties = event.properties || {};
  if (event.event === '$autocapture' && isSensitivePath(properties.$pathname)) return null;
  const scrubbed = { ...event };
  ['properties', '$set', '$set_once'].forEach((bag) => {
    if (!event[bag]) return;
    const copy = { ...event[bag] };
    URL_PROPERTIES.forEach((key) => {
      if (copy[key]) copy[key] = scrubUrl(copy[key]);
    });
    scrubbed[bag] = copy;
  });
  return scrubbed;
};

export const primaryRole = (user) => {
  const roles = (user && user.roles) || {};
  const active = Object.keys(roles).filter((role) => roles[role]);
  return active.length ? active.sort((a, b) => a.localeCompare(b)).join(',') : 'client';
};

export const buildConfig = (host, pathname = '') => ({
  api_host: host || DEFAULT_HOST,
  // A visitor who lands straight on a payment page starts with replay off.
  disable_session_recording: isSensitivePath(pathname),
  capture_pageview: 'history_change',
  capture_pageleave: true,
  capture_exceptions: true,
  person_profiles: 'identified_only',
  session_recording: {
    maskAllInputs: true,
    maskInputOptions: { password: true },
  },
  before_send: beforeSend,
});

// CRA inlines REACT_APP_* only when each variable is named in full.
const buildEnv = () => ({
  REACT_APP_POSTHOG_KEY: process.env.REACT_APP_POSTHOG_KEY,
  REACT_APP_POSTHOG_HOST: process.env.REACT_APP_POSTHOG_HOST,
});

export const initAnalytics = (env = buildEnv()) => {
  const key = (env.REACT_APP_POSTHOG_KEY || '').trim();
  if (!key) return null;
  if (clientPromise) return clientPromise;
  const host = (env.REACT_APP_POSTHOG_HOST || '').trim();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  replayPausedHere = isSensitivePath(pathname);
  clientPromise = import('posthog-js')
    .then((module) => {
      const posthog = module.default || module;
      posthog.init(key, buildConfig(host, pathname));
      posthog.register({ app: APP_NAME });
      return posthog;
    })
    .catch(() => null);
  return clientPromise;
};

const withClient = (fn) => {
  if (!clientPromise) return;
  clientPromise.then((posthog) => {
    if (posthog) fn(posthog);
  });
};

export const identifyUser = (uid, user) => {
  if (!uid) return;
  withClient((posthog) => posthog.identify(uid, { role: primaryRole(user), app: APP_NAME }));
};

export const resetUser = () => withClient((posthog) => posthog.reset());

export const captureException = (error, properties = {}) =>
  withClient((posthog) => posthog.captureException(error, { app: APP_NAME, ...properties }));

export const trackRoute = (pathname) =>
  withClient((posthog) => {
    if (isSensitivePath(pathname)) {
      posthog.stopSessionRecording();
      replayPausedHere = true;
    } else if (replayPausedHere) {
      replayPausedHere = false;
      posthog.startSessionRecording();
    }
  });

// Test helper: forget the loaded client between test cases.
export const resetAnalyticsForTests = () => {
  clientPromise = null;
  replayPausedHere = false;
};
